/* eslint-disable react/prop-types, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import Icon from '../Icon';
import { tokens, orange, inkA } from '../tokens';

// Shared presentational account-select dropdown (the "select style"). It only
// renders + manages open/close; data and on-select behavior come from the
// wrapping variant (TrackAccountSwitcher / AdminAccountSwitcher).
// account shape: { id, initials, name, sub }

function VenueTile({ initials, size = 30 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: Math.round(size * 0.28),
        background: '#fa4616',
        boxShadow: '0 3px 8px rgba(250,70,22,0.34)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: Math.round(size * 0.36),
        fontWeight: 800,
        letterSpacing: '0.3px',
      }}
    >
      {initials}
    </div>
  );
}

function VenueRow({ v, current, onClick }) {
  const theme = useTheme();
  const t = tokens(theme);
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        textAlign: 'left',
        padding: '10px 12px',
        borderRadius: 11,
        cursor: 'pointer',
        border: 'none',
        background: current
          ? orange(0.12)
          : h
          ? inkA(theme, 0.04)
          : 'transparent',
        transition: 'background 120ms ease',
      }}
    >
      <VenueTile initials={v.initials} size={34} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '13.5px',
            fontWeight: 700,
            color: current ? t.primary : t.text,
          }}
        >
          {v.name}
        </div>
        {v.sub ? (
          <div style={{ fontSize: 12, color: t.faint, marginTop: 1 }}>
            {v.sub}
          </div>
        ) : null}
      </div>
      {current && <Icon name="check" size={17} color={t.primary} />}
    </button>
  );
}

const Select = ({ accounts, selectedId, onSelect, headerLabel, footer }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const [open, setOpen] = useState(false);
  const current =
    accounts.find((v) => v.id === selectedId) || accounts[0] || null;

  return (
    <div style={{ position: 'relative' }}>
      <button
        title={headerLabel}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 11,
          height: 46,
          padding: '0 13px',
          borderRadius: 13,
          cursor: 'pointer',
          background: inkA(theme, 0.05),
          border: '1px solid ' + t.border,
          maxWidth: 260,
        }}
      >
        <VenueTile initials={current ? current.initials : '—'} size={30} />
        <span
          style={{
            fontSize: '14.5px',
            fontWeight: 700,
            color: t.text,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {current ? current.name : 'Select account'}
        </span>
        <Icon name="chevrons-up-down" size={17} color={inkA(theme, 0.5)} />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 56,
            right: 0,
            zIndex: 41,
            width: 300,
            background: t.card,
            border: '1px solid ' + t.border,
            borderRadius: 14,
            boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '13px 16px 9px',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: t.label,
            }}
          >
            {headerLabel}
          </div>
          <div
            style={{
              padding: '0 6px 6px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              maxHeight: 360,
              overflowY: 'auto',
            }}
          >
            {accounts.length ? (
              accounts.map((v) => (
                <VenueRow
                  key={v.id}
                  v={v}
                  current={!!current && v.id === current.id}
                  onClick={() => {
                    onSelect(v.id);
                    setOpen(false);
                  }}
                />
              ))
            ) : (
              <div
                style={{ padding: '12px 14px', fontSize: 13, color: t.faint }}
              >
                No accounts found.
              </div>
            )}
          </div>
          {footer && (
            <button
              onClick={() => {
                setOpen(false);
                footer.onClick();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 7,
                width: '100%',
                padding: 13,
                border: 'none',
                borderTop: '1px solid ' + t.line,
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 700,
                color: t.primary,
              }}
            >
              <Icon name="settings-2" size={15} color={t.primary} />{' '}
              {footer.label}
            </button>
          )}
        </div>
      )}

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 40 }}
        />
      )}
    </div>
  );
};

Select.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      initials: PropTypes.string,
      name: PropTypes.string,
      sub: PropTypes.string,
    })
  ).isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func.isRequired,
  headerLabel: PropTypes.string,
  footer: PropTypes.shape({ label: PropTypes.string, onClick: PropTypes.func }),
};

Select.defaultProps = {
  selectedId: null,
  headerLabel: 'Switch account',
  footer: null,
};

export default Select;
